<script setup>
import { computed, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { filterSupportedLanguages } from 'shared/constants/supportedLanguages';

import OptionSelect from 'dashboard/components-next/polaris/cards/OptionSelect.vue';

defineProps({
  label: { type: String, default: '' },
  description: { type: String, default: '' },
});

const { t, locale } = useI18n();
const { updateUISettings, uiSettings } = useUISettings();
const { enabledLanguages } = useConfig();
const { currentAccount } = useAccount();

const currentLanguage = computed(() => uiSettings.value?.locale ?? '');

const languageOptions = computed(() => [
  {
    name: t(
      'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.USE_ACCOUNT_DEFAULT'
    ),
    iso_639_1_code: '',
  },
  ...filterSupportedLanguages(enabledLanguages),
]);

const updateLanguage = async languageCode => {
  try {
    if (!languageCode) {
      // Clear preference to use account default
      await updateUISettings({ locale: null });
      locale.value = currentAccount.value.locale;
      useAlert(
        t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.UPDATE_SUCCESS')
      );
      return;
    }

    const valid = filterSupportedLanguages(enabledLanguages).some(
      l => l.iso_639_1_code === languageCode
    );
    if (!valid) {
      throw new Error(`Invalid language code: ${languageCode}`);
    }

    await updateUISettings({ locale: languageCode });
    // Apply immediately if the user explicitly chose a preference
    locale.value = languageCode;

    useAlert(
      t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.UPDATE_SUCCESS')
    );
  } catch (error) {
    useAlert(
      t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.UPDATE_ERROR')
    );
    throw error;
  }
};

// Phase 9.5: the kit's OptionSelect, as a setting row. It keeps '' for "no value", so the account default is
// the option ACCOUNT_DEFAULT here and '' again when saved.
const ACCOUNT_DEFAULT = 'account-default';
const labelId = `language-${useId()}`;
const options = computed(() =>
  languageOptions.value.map(option => ({
    value: option.iso_639_1_code || ACCOUNT_DEFAULT,
    label: option.name,
  }))
);

const selectedValue = computed({
  get: () => currentLanguage.value || ACCOUNT_DEFAULT,
  set: value => {
    updateLanguage(value === ACCOUNT_DEFAULT ? '' : value);
  },
});
</script>

<template>
  <div
    class="flex w-full flex-wrap items-start justify-between gap-x-4 gap-y-2"
  >
    <div class="min-w-0 flex-1 basis-48">
      <p :id="labelId" class="type-polaris-label">{{ label }}</p>
      <p class="type-polaris-caption text-polaris-muted-foreground">
        {{ description }}
      </p>
    </div>
    <OptionSelect
      v-model="selectedValue"
      :options="options"
      :aria-labelledby="labelId"
      class="w-full sm:w-48"
    />
  </div>
</template>
